import React from "react";
import { REPORT_REASON_LABELS, timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * Reports tab: safety moderation queue with the same resolutions and actions as before.
 *
 * Extracted verbatim from src/pages/Admin.jsx during the code-quality pass:
 * markup, copy, data-testids and behaviour are unchanged - only the file
 * boundary moved.
 */
export default function AdminReports({ reports, act, resolve }) {
  return (
        <div className="mt-6 space-y-4" data-testid="admin-reports">
          {reports.length === 0 ? (
            <p className="text-sm text-[#4B5A60]">No reports have been filed.</p>
          ) : (
            reports.map((r) => (
              <div
                key={r.id}
                data-testid={`admin-report-${r.id}`}
                className={`sm-card p-5 ${r.status === "open" ? "bg-[#FDF9F4] border-[#E7CF9E]" : "bg-white"}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#1F2A2E]">
                      {r.reporter?.display_name || "A member"} reported{" "}
                      {r.reported_user?.display_name || "a member"}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                      <span className="px-2 py-0.5 rounded-full bg-[#FBE6E4] text-[#B42318] font-semibold">
                        {REPORT_REASON_LABELS[r.reason] || r.reason}
                      </span>
                      <span className="text-[#7A868B]">{timeAgo(r.created_at)}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full font-semibold ${
                          r.status === "open"
                            ? "bg-[#FBF0DC] text-[#8A5A12]"
                            : "bg-[#E4F3EA] text-[#14603C]"
                        }`}
                      >
                        {r.status}
                      </span>
                    </div>
                  </div>
                  {r.status === "open" && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => act(r.reported_user_id, "warn")}
                        className="bg-[#B7791F] hover:bg-[#95610F] text-white"
                      >
                        Warn
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => act(r.reported_user_id, "suspend")}
                        className="bg-[#D07A4A] hover:bg-[#B96536] text-white"
                      >
                        Suspend
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => act(r.reported_user_id, "ban")}
                        className="bg-[#B42318] hover:bg-[#94180F] text-white"
                      >
                        Ban
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resolve(r.id, "resolved", "reviewed")}
                        data-testid={`resolve-report-${r.id}`}
                        className="bg-white border-[#E3DED4]"
                      >
                        Mark resolved
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => resolve(r.id, "dismissed")}
                        className="text-[#4B5A60]"
                      >
                        Dismiss
                      </Button>
                    </div>
                  )}
                </div>
                {r.details ? (
                  <p className="mt-3 text-sm text-[#4B5A60] border-l-2 border-[#E3DED4] pl-3">
                    {r.details}
                  </p>
                ) : null}
                {r.reported_message ? (
                  <div className="mt-3 rounded-xl bg-white border border-[#E3DED4] p-3">
                    <p className="text-xs font-bold text-[#4B5A60]">Reported message</p>
                    <p className="mt-1 text-sm text-[#1F2A2E]">{r.reported_message.message_text}</p>
                  </div>
                ) : null}
                {r.conversation_evidence?.length ? (
                  <details className="mt-3">
                    <summary className="text-sm font-semibold text-[#1F6F78] cursor-pointer">
                      View recent conversation ({r.conversation_evidence.length} messages)
                    </summary>
                    <div className="mt-2 space-y-1.5 max-h-64 overflow-y-auto sm-scroll">
                      {r.conversation_evidence.map((m) => (
                        <p key={m.id} className="text-xs text-[#4B5A60] bg-white rounded-lg px-3 py-2 border border-[#E3DED4]">
                          <span className="font-semibold">
                            {m.sender_id === r.reported_user_id ? "Reported member" : "Reporter"}:
                          </span>{" "}
                          {m.message_text}
                        </p>
                      ))}
                    </div>
                  </details>
                ) : null}
              </div>
            ))
          )}
        </div>
  );
}
